import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Card,
  Button,
  Form,
  Row,
  Col,
  Accordion,
} from "react-bootstrap";
import Select from "react-select";
import Pagination from "../../components/Pagination";
import useClientTableSort, {
  sortRowsByConfig,
} from "../../hooks/useClientTableSort";
import useAccess from "../../hooks/useAccess";

const ViolationList = () => {
  const [records, setRecords] = useState([]);
  const [recordsUnfiltered, setRecordsUnfiltered] = useState([]);
  const [filterType, setFilterType] = useState(null);
  const [filterNumber, setFilterNumber] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [filterSavedBy, setFilterSavedBy] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const tableRef = useRef(null);
  const itemsPerPage = 20;

  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const role = localStorage.getItem("userRole");
  const isAdmin = role === "1";
  const { hasPermission } = useAccess();
  const userId = isAdmin && paramId ? paramId : localStorage.getItem("userId");
  const canDeleteRecords = isAdmin && hasPermission("Task", "Task", "delete");

  const violationTypeOptions = [
    { label: "A Warning", value: "A Warning" },
    { label: "Absence", value: "Absence" },
    { label: "Company Policy", value: "Company Policy" },
    {
      label: "Failure to Follow Procedure",
      value: "Failure to Follow Procedure",
    },
    { label: "Failure to Notify", value: "Failure to Notify" },
    { label: "HOS Violation", value: "HOS Violation" },
    { label: "Other", value: "Other" },
    { label: "Ticket Citation", value: "Ticket Citation" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        };
        const res = await axios.get(`${API_BASE}/api/violations/${userId}`, {
          headers,
        });
        setRecordsUnfiltered(res.data);
        setRecords(res.data);
      } catch (err) {
        console.error("Fetch error", err);
      }
    };
    fetchData();
  }, [API_BASE, userId]);

  const applyFilters = () => {
    let filtered = [...recordsUnfiltered];

    if (filterType?.length) {
      const selectedTypes = filterType.map((t) => t.value);
      filtered = filtered.filter((i) => selectedTypes.includes(i.viol_type));
    }

    if (filterNumber) {
      filtered = filtered.filter((i) =>
        i.viol_number?.toLowerCase().includes(filterNumber.toLowerCase())
      );
    }

    if (filterDate) {
      filtered = filtered.filter(
        (i) =>
          new Date(i.viol_date).toDateString() ===
          new Date(filterDate).toDateString()
      );
    }

    if (filterSavedBy?.length) {
      const selectedUsers = filterSavedBy.map((s) => s.value);
      filtered = filtered.filter((i) =>
        selectedUsers.includes(i.saved_by_name)
      );
    }

    setRecords(filtered);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilterType(null);
    setFilterNumber("");
    setFilterDate("");
    setFilterSavedBy(null);
    setRecords(recordsUnfiltered);
  };

  const sortConfig = useClientTableSort(tableRef, () => setCurrentPage(1));
  const sortedRecords = sortRowsByConfig(records, sortConfig, [
    (record) => record.viol_type,
    (record) => record.viol_number,
    (record) => record.viol_date,
    (record) => record.viol_saved_date,
    (record) => record.saved_by_name || "-",
  ]);

  const paginatedRecords = sortedRecords.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const savedByOptions = [
    ...new Set(recordsUnfiltered.map((i) => i.saved_by_name).filter(Boolean)),
  ].map((s) => ({ label: s, value: s }));

  const handleCancel = () => {
    if (isAdmin && paramId) {
      navigate(`/userhome/${paramId}`);
    } else {
      navigate("/userhome");
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!canDeleteRecords || !recordId) return;
    if (!window.confirm("Delete this record? This cannot be undone.")) return;

    try {
      await axios.delete(`${API_BASE}/api/violations/${recordId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setRecords((prev) => prev.filter((row) => row.viol_id !== recordId));
      setRecordsUnfiltered((prev) => prev.filter((row) => row.viol_id !== recordId));
    } catch (err) {
      console.error("Violation delete failed", err);
      alert(err?.response?.data?.message || "Failed to delete record.");
    }
  };

  return (
    <Container className="form-wrapper my-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0">Violations</h3>
        <div>
          <Button variant="secondary" className="me-2" onClick={handleCancel}>
            Return
          </Button>
          <Button
            variant="success"
            onClick={() => navigate(`/violations/${userId}/violation-new`)}
          >
            Add
          </Button>
        </div>
      </div>

      <Card>
        <Card.Body>
          <Accordion defaultActiveKey="">
            <Accordion.Item eventKey="0">
              <Accordion.Header>Filters</Accordion.Header>
              <Accordion.Body>
                <Form className="mb-4">
                  <Row className="align-items-end g-3">
                    <Col md={3}>
                      <Form.Label>Violation Type</Form.Label>
                      <Select
                        isMulti
                        options={violationTypeOptions}
                        value={filterType}
                        onChange={setFilterType}
                        placeholder="Select Type(s)"
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label>Violation No.</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Search Number"
                        value={filterNumber}
                        onChange={(e) => setFilterNumber(e.target.value)}
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label>Violation Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={filterDate}
                        onChange={(e) => setFilterDate(e.target.value)}
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label>Saved By</Form.Label>
                      <Select
                        isMulti
                        options={savedByOptions}
                        value={filterSavedBy}
                        onChange={setFilterSavedBy}
                        placeholder="Select Saved By"
                      />
                    </Col>
                    <Col md={3} className="d-flex gap-2">
                      <Button
                        variant="secondary"
                        onClick={clearFilters}
                        className="mt-3"
                      >
                        Clear Filter
                      </Button>
                      <Button
                        variant="success"
                        onClick={applyFilters}
                        className="mt-3"
                      >
                        Filter
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          {records.length === 0 ? (
            <p className="mt-3 text-muted fst-italic">No violations found.</p>
          ) : (
            <div className="table-responsive mt-3">
              <table
                ref={tableRef}
                className="table table-bordered align-middle text-center"
                data-nw-client-sort="true"
              >
                <thead className="table-light">
                  <tr>
                    <th className="text-center">Type of Violation</th>
                    <th>Violation No.</th>
                    <th>Violation Date</th>
                    <th>Date Saved</th>
                    <th>Saved By</th>
                    <th className="text-center" style={{ width: "110px" }}>
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRecords.map((v) => (
                    <tr key={v.viol_id}>
                      <td className="text-center">{v.viol_type}</td>
                      <td>{v.viol_number}</td>
                      <td>
                        {v.viol_date
                          ? new Date(v.viol_date).toLocaleDateString()
                          : "-"}
                      </td>
                      <td>
                        {v.viol_saved_date
                          ? new Date(v.viol_saved_date).toLocaleDateString()
                          : "-"}
                      </td>
                      <td>{v.saved_by_name || "-"}</td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() =>
                              navigate(
                                `/violations/${userId}/violation-detail/${v.viol_id}`
                              )
                            }
                          >
                            Edit
                          </Button>
                          {canDeleteRecords && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteRecord(v.viol_id)}
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Pagination
                currentPage={currentPage}
                totalItems={records.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ViolationList;
